/**
 * FocusManager — on every route change, moves focus to the new page's `<h1>`
 * and announces the change via a polite `aria-live` region.
 *
 * Fixes the React SPA a11y footgun where screen reader users stay on the old
 * step's DOM after navigation. Per rcp-security §B.5 #1.
 *
 * Usage: mount once inside the BrowserRouter tree. The companion `<LiveRegion/>`
 * renders a visually-hidden aria-live region into which this writes messages.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

/**
 * Writes the current document title (or first h1) to a polite live region
 * on every URL change. Focuses the first h1 so SR users land at the top.
 */
export function FocusManager(): JSX.Element {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState('');
  const initialMount = useRef(true);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }

    // Defer so React has time to render the new route.
    const handle = window.setTimeout(() => {
      const heading = document.querySelector<HTMLElement>('main h1');
      if (heading) {
        // Make the heading focusable if not already, then focus it silently.
        if (!heading.hasAttribute('tabindex')) {
          heading.setAttribute('tabindex', '-1');
        }
        heading.focus({ preventScroll: false });
      }
      const title = document.title || 'Page changed';
      setAnnouncement(`${title} loaded`);
    }, 60);

    return () => window.clearTimeout(handle);
  }, [location.pathname]);

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="visually-hidden"
      data-testid="route-announcer"
    >
      {announcement}
    </div>
  );
}
